/**
 * IDDLSettings — Iter-17 candidate-facing simulation primitive.
 *
 * Realistic device/system "Settings" workspace for CS Safe-Behaviour
 * concepts that measure configuration judgement (passwords, MFA,
 * device security, Wi-Fi/VPN, updates, privacy). Designed to feel
 * like a real OS preferences pane, not a multiple-choice quiz:
 *
 *   • left rail  — section tabs (Account · Devices · Network · Privacy)
 *                  with an unsafe-state count badge per section
 *   • centre     — list of settings in the active section, each
 *                  rendered as a real control (toggle / radio / select /
 *                  password). Unsafe values produce an inline warning
 *                  card the candidate must either acknowledge OR
 *                  reverse before submitting. Recovery is ALWAYS
 *                  allowed.
 *   • footer     — final-decision bar with a live "N unsafe settings
 *                  in this configuration" summary
 *
 * Canonical events:
 *   settings_opened          — once on mount
 *   section_opened           — switching tabs
 *   setting_changed          — every value change (research signal)
 *   unsafe_value_attempted   — change selected a value in unsafe_values
 *   warning_acknowledged     — explicit "Got it" on the warning card
 *   setting_reverted         — unsafe → safe transition
 *   warning_dwell_ms         — research signal (time on warning card)
 *   setting_secured          — emitted PER SETTING at final_decision
 *                              when current_value === correct_value
 *                              (the multi-object scorer keys on this)
 *   final_decision           — submit final configuration
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ShieldCheck, KeyRound, Smartphone, Wifi, Lock,
  AlertTriangle, RotateCcw, CheckCircle2, Eye, EyeOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";

const SECTION_ICONS = {
  account: KeyRound,
  device: Smartphone,
  network: Wifi,
  privacy: Lock,
};

// Helper — does a value count as unsafe for this setting?
function isUnsafeValue(setting, value) {
  const unsafe = setting.unsafe_values || [];
  return unsafe.includes(value);
}

export default function IDDLSettings({
  attemptId,
  conceptId,
  scenarioText,
  onFinalize,
  onEventLogged,
}) {
  const { t } = useT();

  const sections = scenarioText?.sections || [];
  const settingsCatalogue = scenarioText?.settings || [];
  const settingsById = useMemo(() => {
    const map = {};
    for (const s of settingsCatalogue) map[s.id] = s;
    return map;
  }, [settingsCatalogue]);

  const initialValues = useMemo(() => {
    const out = {};
    for (const s of settingsCatalogue) out[s.id] = s.initial_value;
    return out;
  }, [settingsCatalogue]);

  const [activeSectionId, setActiveSectionId] = useState(sections[0]?.id);
  const [values, setValues] = useState(initialValues);
  const [acknowledged, setAcknowledged] = useState({}); // {setting_id: true}
  const [activeWarning, setActiveWarning] = useState(null); // {setting_id, since_ms}
  const [showPassword, setShowPassword] = useState({}); // {setting_id: true}
  const [finalized, setFinalized] = useState(null);
  const openedRef = useRef(false);

  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    emit("settings_opened", {
      section_count: sections.length,
      setting_count: settingsCatalogue.length,
    }, "settings");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function emit(eventName, eventValue, screenContext) {
    try {
      const r = await api
        .post(`/attempts/${attemptId}/events`, {
          concept_id: conceptId,
          event_name: eventName,
          event_value: eventValue,
          screen_context: screenContext,
          client_ts_ms: Date.now(),
        })
        .then((x) => x.data);
      if (onEventLogged && r?.event) onEventLogged(r.event);
      return r?.event;
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Event failed";
      toast.error(`Event ${eventName} failed: ${detail}`);
      return null;
    }
  }

  // Compute the per-section unsafe count for the rail badges.
  const unsafeCountBySection = useMemo(() => {
    const out = {};
    for (const sec of sections) out[sec.id] = 0;
    for (const s of settingsCatalogue) {
      if (isUnsafeValue(s, values[s.id])) {
        out[s.section_id] = (out[s.section_id] || 0) + 1;
      }
    }
    return out;
  }, [sections, settingsCatalogue, values]);

  const totalUnsafeCount = Object.values(unsafeCountBySection)
    .reduce((a, b) => a + b, 0);

  const onSectionClick = (sec) => {
    if (sec.id === activeSectionId) return;
    setActiveSectionId(sec.id);
    emit("section_opened", { section_id: sec.id }, "settings");
  };

  const onChange = (setting, nextValue) => {
    if (finalized) return;
    const prev = values[setting.id];
    if (prev === nextValue) return;
    setValues((v) => ({ ...v, [setting.id]: nextValue }));
    emit("setting_changed", {
      setting_id: setting.id, from: prev, to: nextValue,
    }, "settings");

    const wasUnsafe = isUnsafeValue(setting, prev);
    const nowUnsafe = isUnsafeValue(setting, nextValue);
    if (nowUnsafe) {
      emit("unsafe_value_attempted", {
        setting_id: setting.id, value: nextValue,
        risk_level: setting.risk_level || "warn",
      }, "settings");
      setActiveWarning({ setting_id: setting.id, since_ms: Date.now() });
      setAcknowledged((s) => ({ ...s, [setting.id]: false }));
    } else if (wasUnsafe && !nowUnsafe) {
      emit("setting_reverted", {
        setting_id: setting.id, from: prev, to: nextValue,
      }, "settings");
      // Close any open warning for this setting.
      if (activeWarning?.setting_id === setting.id) {
        const dwellMs = Date.now() - (activeWarning.since_ms || Date.now());
        emit("warning_dwell_ms", {
          setting_id: setting.id, dwell_ms: dwellMs,
        }, "settings");
        setActiveWarning(null);
      }
    }
  };

  const acknowledgeWarning = (setting) => {
    if (!activeWarning || activeWarning.setting_id !== setting.id) return;
    const dwellMs = Date.now() - (activeWarning.since_ms || Date.now());
    emit("warning_acknowledged", {
      setting_id: setting.id,
      value: values[setting.id],
      dwell_ms: dwellMs,
    }, "settings");
    emit("warning_dwell_ms", {
      setting_id: setting.id, dwell_ms: dwellMs,
    }, "settings");
    setAcknowledged((s) => ({ ...s, [setting.id]: true }));
    setActiveWarning(null);
  };

  const revertToSafe = (setting) => {
    // Convenience: when the warning offers a "Revert" CTA, set the
    // first non-unsafe option (or empty string for free-text).
    const opts = setting.options || [];
    const safe = opts.find((o) => !isUnsafeValue(setting, o.value));
    onChange(setting, safe ? safe.value : "");
  };

  const submitFinal = () => {
    if (finalized) return;
    // For every setting whose final value equals correct_value, emit
    // a per-setting `setting_secured` event so the multi-object scorer
    // can count it as a successful "report" for that setting_id.
    for (const s of settingsCatalogue) {
      if (values[s.id] === s.correct_value) {
        emit("setting_secured", {
          setting_id: s.id, message_id: s.id, value: values[s.id],
        }, "settings");
      }
    }
    emit("final_decision", {
      decision: "submitted",
      final_state: { ...values },
      unsafe_remaining: totalUnsafeCount,
    }, "settings");
    setFinalized("submitted");
    onFinalize?.({ decision: "submitted" });
  };

  // ─── Control renderers ───────────────────────────────────────────────
  function renderControl(s) {
    const val = values[s.id];
    const unsafe = isUnsafeValue(s, val);
    const cls = `border ${unsafe ? "border-rose-400" : "border-[var(--iddl-rule)]"} bg-white text-sm px-2 py-1.5 w-full`;

    if (s.control_type === "toggle") {
      const onVal = s.on_value ?? true;
      const offVal = s.off_value ?? false;
      const isOn = val === onVal;
      return (
        <button
          type="button"
          onClick={() => onChange(s, isOn ? offVal : onVal)}
          disabled={!!finalized}
          className={`inline-flex items-center gap-2 px-3 py-1.5 border-2 ${
            isOn
              ? "border-emerald-500 bg-emerald-50 text-emerald-800"
              : "border-zinc-300 bg-white text-zinc-700"
          } ${unsafe ? "ring-2 ring-rose-300" : ""}`}
          data-testid={`iddl-set-toggle-${s.id}`}
        >
          <span className="w-2.5 h-2.5 rounded-full"
            style={{ background: isOn ? "#059669" : "#a1a1aa" }} />
          {isOn ? t("sim.set.on") : t("sim.set.off")}
        </button>
      );
    }

    if (s.control_type === "radio" || s.control_type === "select") {
      return (
        <div className="space-y-1.5">
          {(s.options || []).map((opt) => {
            const isOptUnsafe = isUnsafeValue(s, opt.value);
            const active = val === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange(s, opt.value)}
                disabled={!!finalized}
                className={`w-full text-left text-sm px-2 py-1.5 border-2 ${
                  active
                    ? (isOptUnsafe
                        ? "border-rose-500 bg-rose-50"
                        : "border-emerald-500 bg-emerald-50")
                    : "border-zinc-200 bg-white hover:bg-[var(--iddl-paper)]"
                }`}
                data-testid={`iddl-set-opt-${s.id}-${opt.value}`}
              >
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full border border-zinc-400"
                    style={{ background: active ? "#0f172a" : "transparent" }} />
                  <span className="flex-1">{opt.label}</span>
                  {isOptUnsafe && (
                    <span className="text-[10px] font-mono text-rose-700 px-1.5 border border-rose-300 bg-rose-50">
                      {t("sim.set.unsafe_tag")}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      );
    }

    if (s.control_type === "password") {
      const visible = !!showPassword[s.id];
      return (
        <div className="flex items-center gap-1">
          <input
            type={visible ? "text" : "password"}
            value={val || ""}
            disabled={!!finalized}
            onChange={(e) => onChange(s, e.target.value)}
            className={cls}
            placeholder={s.placeholder || "—"}
            data-testid={`iddl-set-password-${s.id}`}
          />
          <button
            type="button"
            onClick={() => setShowPassword((sp) => ({ ...sp, [s.id]: !visible }))}
            className="border border-[var(--iddl-rule)] p-1.5 bg-white"
            data-testid={`iddl-set-password-toggle-${s.id}`}
          >
            {visible ? <EyeOff size={12} /> : <Eye size={12} />}
          </button>
        </div>
      );
    }

    // text fallback
    return (
      <input
        type="text"
        value={val || ""}
        disabled={!!finalized}
        onChange={(e) => onChange(s, e.target.value)}
        className={cls}
        data-testid={`iddl-set-text-${s.id}`}
      />
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────
  const activeSection = sections.find((s) => s.id === activeSectionId);
  const sectionSettings = settingsCatalogue.filter(
    (s) => s.section_id === activeSectionId,
  );

  return (
    <div
      className="border border-[var(--iddl-rule)] grid grid-cols-12 min-h-[560px] bg-white"
      data-testid="iddl-settings"
    >
      {/* Left rail — sections */}
      <aside
        className="col-span-3 border-r border-[var(--iddl-rule)] bg-[var(--iddl-paper)] p-3"
        data-testid="iddl-set-section-rail"
      >
        <div className="label-mono mb-2 flex items-center gap-2">
          <ShieldCheck size={12} />
          {t("sim.set.workspace_title")}
        </div>
        {sections.map((sec) => {
          const Icon = SECTION_ICONS[sec.icon] || ShieldCheck;
          const unsafeCount = unsafeCountBySection[sec.id] || 0;
          return (
            <button
              key={sec.id}
              type="button"
              onClick={() => onSectionClick(sec)}
              className={`w-full flex items-center gap-2 text-left text-sm px-2 py-1.5 mb-1 border-l-2 ${
                sec.id === activeSectionId
                  ? "border-[var(--iddl-ink)] bg-white"
                  : "border-transparent hover:bg-white"
              }`}
              data-testid={`iddl-set-section-${sec.id}`}
            >
              <Icon size={14} />
              <span className="truncate flex-1">{sec.label}</span>
              {unsafeCount > 0 && (
                <span
                  className="text-[10px] font-mono px-1.5 border border-rose-300 bg-rose-100 text-rose-700"
                  data-testid={`iddl-set-section-unsafe-${sec.id}`}
                >
                  {unsafeCount}
                </span>
              )}
            </button>
          );
        })}

        {/* Bottom: total unsafe summary */}
        <div className="mt-4 pt-3 border-t border-[var(--iddl-rule)] text-xs">
          <div className="label-mono mb-1">{t("sim.set.summary_title")}</div>
          {totalUnsafeCount > 0 ? (
            <div className="text-rose-700 flex items-center gap-1.5" data-testid="iddl-set-summary-unsafe">
              <AlertTriangle size={12} />
              {t("sim.set.unsafe_count", { n: totalUnsafeCount })}
            </div>
          ) : (
            <div className="text-emerald-700 flex items-center gap-1.5" data-testid="iddl-set-summary-secure">
              <CheckCircle2 size={12} />
              {t("sim.set.all_secure")}
            </div>
          )}
        </div>
      </aside>

      {/* Centre — active section's settings */}
      <section
        className="col-span-9 p-4 flex flex-col gap-3 overflow-y-auto"
        data-testid="iddl-set-section-body"
      >
        <div className="label-mono mb-1">{activeSection?.label}</div>
        {activeSection?.description && (
          <div className="text-sm text-[var(--iddl-muted)] mb-1">
            {activeSection.description}
          </div>
        )}

        {sectionSettings.map((s) => {
          const unsafe = isUnsafeValue(s, values[s.id]);
          const isWarningOpen =
            activeWarning?.setting_id === s.id && unsafe && !acknowledged[s.id];
          return (
            <div
              key={s.id}
              className={`border ${
                unsafe ? "border-rose-300 bg-rose-50/30" : "border-[var(--iddl-rule)] bg-white"
              } p-3`}
              data-testid={`iddl-set-row-${s.id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1">
                  <div className="font-semibold text-sm">{s.label}</div>
                  {s.description && (
                    <div className="text-xs text-[var(--iddl-muted)] mt-0.5">
                      {s.description}
                    </div>
                  )}
                </div>
                {unsafe && (
                  <span
                    className="text-[10px] font-mono px-1.5 py-0.5 border border-rose-400 bg-rose-100 text-rose-700"
                    data-testid={`iddl-set-row-unsafe-${s.id}`}
                  >
                    {t("sim.set.unsafe_tag")}
                  </span>
                )}
              </div>

              <div>{renderControl(s)}</div>

              {/* Inline warning card — recoverable, ack-able. */}
              {isWarningOpen && (
                <div
                  className="mt-3 border-2 border-rose-500 bg-rose-50 p-3"
                  data-testid={`iddl-set-warning-${s.id}`}
                >
                  <div className="flex items-start gap-2 mb-2">
                    <AlertTriangle size={14} className="text-rose-700 mt-0.5" />
                    <div className="text-sm text-rose-900">
                      <div className="font-semibold mb-1">
                        {t("sim.set.warning_title")}
                      </div>
                      <div className="text-xs">
                        {s.warning_text || t("sim.set.warning_default")}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={() => revertToSafe(s)}
                      size="sm"
                      variant="outline"
                      className="rounded-none border-emerald-500 text-emerald-800 hover:bg-emerald-50"
                      data-testid={`iddl-set-warning-revert-${s.id}`}
                    >
                      <RotateCcw size={12} className="mr-1" />
                      {t("sim.set.warning_revert")}
                    </Button>
                    <Button
                      onClick={() => acknowledgeWarning(s)}
                      size="sm"
                      className="rounded-none btn-ink"
                      data-testid={`iddl-set-warning-ack-${s.id}`}
                    >
                      {t("sim.set.warning_ack")}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Footer — final decision */}
        <div
          className="mt-auto pt-3 border-t border-[var(--iddl-rule)] flex items-center gap-3"
          data-testid="iddl-set-final"
        >
          <div className="text-sm flex-1">
            {totalUnsafeCount > 0 ? (
              <span className="text-rose-700 flex items-center gap-1.5">
                <AlertTriangle size={12} />
                {t("sim.set.unsafe_count", { n: totalUnsafeCount })}
              </span>
            ) : (
              <span className="text-emerald-700 flex items-center gap-1.5">
                <CheckCircle2 size={12} />
                {t("sim.set.all_secure")}
              </span>
            )}
          </div>
          <Button
            onClick={submitFinal}
            disabled={!!finalized}
            className="rounded-none btn-ink"
            data-testid="iddl-set-btn-submit"
          >
            <Lock size={12} className="mr-2" />
            {t("sim.set.btn_submit")}
          </Button>
        </div>
        {finalized && (
          <div className="text-xs text-[var(--iddl-muted)]" data-testid="iddl-set-finalized">
            {t("sim.set.finalized", { decision: finalized })}
          </div>
        )}
      </section>
    </div>
  );
}
